import { type ReactNode, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useLocale } from '../../shared/i18n/LocaleContext'
import {
  LocalizedDecimalInput,
  LocalizedDateInput,
  LocalizedTimeInput,
} from '../../shared/i18n/LocalizedInputs'
import { capacityCommand, getCapacity, getCapacityDeletionAvailability } from './capacityApi'
import { AppModal } from '../../shared/components/AppModal'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { PagedListControls } from '../../shared/components/PagedListControls'
import { PageLoading } from '../../shared/components/PageLoading'
import { listParams, SortableHeaders, useUrlSortedRows } from '../../shared/components/SortableTableHeader'
import { getPreferredPageSize, rememberPageSize } from '../../shared/pagination/pageSizePreference'
import { IntegrationProtectionBoundary } from '../../shared/integrations/IntegrationManagedField'
import { useIntegrationProtection } from '../../shared/integrations/useIntegrationProtection'
import type {
  CalendarItem,
  CalendarException,
  CalendarShift,
  CapacitySnapshot,
  ProductionUnitItem,
  ResourceItem,
  WorkCenterItem,
} from './types'

type Tab = 'productionUnits' | 'workCenters' | 'resources' | 'calendars'
export type CapacitySection = Tab | 'overview'
type Modal = Tab | null

const capacityTabs = [
  {
    key: 'productionUnits',
    descriptionKey: 'capacity.productionUnitsDescription',
    route: '/app/recursos/estructura',
  },
  {
    key: 'workCenters',
    descriptionKey: 'capacity.workCentersDescription',
    route: '/app/recursos/centros-trabajo',
  },
  {
    key: 'resources',
    descriptionKey: 'capacity.resourcesDescription',
    route: '/app/recursos/listado',
  },
  {
    key: 'calendars',
    descriptionKey: 'capacity.calendarsDescription',
    route: '/app/recursos/calendarios',
  },
] as const

const defaultShifts: CalendarShift[] = [1, 2, 3, 4, 5].map((dayOfWeek) => ({
  dayOfWeek,
  startMinute: 480,
  endMinute: 960,
  capacityPercent: 100, efficiencyPercent: 100, yieldPercent: 100,
}))

const resourceKinds = [
  'Machine', 'Labour', 'ProductionLine', 'Workstation', 'Tool', 'Subcontractor',
] as const
const productionUnitKinds = [
  'Plant', 'Area', 'Section', 'ProductionLine', 'Cell', 'Warehouse', 'Location',
] as const

export function CapacityPage({ section = 'overview' }: { section?: CapacitySection }) {
  const { user } = useAuth()
  const { t, formatNumber, locale } = useLocale()
  const { confirm, confirmationDialog } = useAppConfirm()
  const navigate = useNavigate()
  const canEdit = user?.permissions.capacity === 'Write'
  const entityByTab = { productionUnits: 'ProductionUnit', workCenters: 'WorkCenter', resources: 'Resource', calendars: 'ProductionCalendar' } as const
  const activeProtection = useIntegrationProtection(entityByTab[section === 'overview' ? 'workCenters' : section])
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1)
  const pageSize = getPreferredPageSize(searchParams)
  const appliedQuery = searchParams.get('q') ?? ''
  const appliedStatus = searchParams.get('status') ?? ''
  const [query, setQuery] = useState(appliedQuery)
  const [status, setStatus] = useState(appliedStatus)
  useEffect(() => { setQuery(appliedQuery); setStatus(appliedStatus) },
    [appliedQuery, appliedStatus])
  const [snapshot, setSnapshot] = useState<CapacitySnapshot | null>(null)
  const tab = section === 'overview' ? null : section
  const activeTab: Tab = tab ?? 'workCenters'
  const [modal, setModal] = useState<Modal>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [center, setCenter] = useState<WorkCenterItem | null>(null)
  const [productionUnit, setProductionUnit] =
    useState<ProductionUnitItem | null>(null)
  const [resource, setResource] = useState<ResourceItem | null>(null)
  const [calendar, setCalendar] = useState<CalendarItem | null>(null)

  const load = async () => {
    setError(null)
    try { setSnapshot(await getCapacity(section === 'overview' ? undefined : {
      section, query: appliedQuery, status: appliedStatus, page, pageSize,
    })) }
    catch (reason) {
      setError(reason instanceof Error ? reason.message : 'capacity.loadError')
    }
  }
  useEffect(() => { void load() }, [user?.activeCompanyId, section,
    appliedQuery, appliedStatus, page, pageSize])
  useEffect(() => {
    const id = searchParams.get('edit')
    if (!snapshot) return
    setError(null)
    if (!tab) { setModal(null); setCenter(null); setProductionUnit(null); setResource(null); setCalendar(null); return }
    if (searchParams.get('new') === '1' && canEdit && activeProtection.canCreate) { setModal(tab); return }
    if (!id) { setModal(null); setCenter(null); setProductionUnit(null); setResource(null); setCalendar(null); return }
    if (tab === 'productionUnits') setProductionUnit(snapshot.productionUnits.find(item => item.id === id) ?? null)
    if (tab === 'workCenters') setCenter(snapshot.workCenters.find(item => item.id === id) ?? null)
    if (tab === 'resources') setResource(snapshot.resources.find(item => item.id === id) ?? null)
    if (tab === 'calendars') setCalendar(snapshot.calendars.find(item => item.id === id) ?? null)
    if (snapshot[tab].some(item => item.id === id)) setModal(tab)
    else { setModal(null); setCenter(null); setProductionUnit(null); setResource(null); setCalendar(null) }
  }, [activeProtection.canCreate, canEdit, searchParams, snapshot, tab])

  const closeEditor = () => {
    setError(null); setModal(null); setCenter(null); setProductionUnit(null); setResource(null); setCalendar(null)
    const next = new URLSearchParams(searchParams); next.delete('edit'); next.delete('new')
    setSearchParams(next, { replace: true })
  }
  const openNew = () => {
    const next = new URLSearchParams(searchParams); next.delete('edit'); next.set('new', '1')
    setSearchParams(next)
  }

  const execute = async (action: () => Promise<{code:string;planningScenarioRequired:boolean}>) => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const result = await action()
      setMessage(result.code)
      await load()
      closeEditor()
      if (result.planningScenarioRequired && user?.permissions.planning === 'Write' &&
          await confirm(t('planning.changeRequiresSimulation'))) {
        navigate('/app/planificacion?scenario=global')
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'capacity.actionError')
    } finally { setBusy(false) }
  }
  const remove = async (path: string) => {
    setError(null)
    try {
      const availability = await getCapacityDeletionAvailability(path)
      if (!availability.allowed) { setError(availability.code); return }
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'deletion.unavailable'); return }
    if (!await confirm(t('deletion.confirm'), undefined, true)) return
    await execute(() => capacityCommand(path, 'DELETE'))
  }
  const unitSort=useUrlSortedRows(snapshot?.productionUnits??[],{code:x=>x.code,name:x=>productionUnitPath(x,snapshot?.productionUnits??[]),kind:x=>x.kind,parent:x=>snapshot?.productionUnits.find(p=>p.id===x.parentId)?.name,centers:x=>x.workCenterCount,status:x=>x.isActive},'code','asc',locale)
  const centerSort=useUrlSortedRows(snapshot?.workCenters??[],{code:x=>x.code,name:x=>x.name,planning:x=>x.planningMode,capacity:x=>x.capacityMode,efficiency:x=>x.efficiencyPercent,resources:x=>x.resourceCount,status:x=>x.isActive},'code','asc',locale)
  const resourceSort=useUrlSortedRows(snapshot?.resources??[],{code:x=>x.code,name:x=>x.name,kind:x=>x.kind,center:x=>snapshot?.workCenters.find(c=>c.id===x.workCenterId)?.name,parallel:x=>x.parallelUnits,efficiency:x=>x.efficiencyPercent,status:x=>x.isActive},'code','asc',locale)
  const calendarSort=useUrlSortedRows(snapshot?.calendars??[],{code:x=>x.code,name:x=>x.name,timeZone:x=>x.timeZoneId,hours:x=>x.shifts.reduce((total,shift)=>total+shift.endMinute-shift.startMinute,0),status:x=>x.isActive},'code','asc',locale)

  if (!snapshot) return <PageLoading label={t('capacity.loading')}
    error={error ? t(error as Parameters<typeof t>[0]) : null}/>

  return <main className="page-content">
    <header className="welcome administration-heading">
      <div>
        <span className="eyebrow">{t('capacity.eyebrow')}</span>
        <h1>{t('capacity.title')}</h1>
        <p>{snapshot.companyName} · {t('capacity.subtitle')}</p>
      </div>
    </header>
    {error && !modal && <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>}
    {message && <p className="form-success">{t(message as Parameters<typeof t>[0])}</p>}
    {section === 'overview' ? <section className="capacity-overview-grid">
      {capacityTabs.map(item => <Link className="capacity-overview-card" to={item.route} key={item.key}>
        <span className="capacity-overview-count">{formatNumber(snapshot.overview[item.key])}</span>
        <h2>{t(`capacity.${item.key}`)}</h2>
        <p>{t(item.descriptionKey)}</p>
        <span className="capacity-overview-link">{t('capacity.openSection')} →</span>
      </Link>)}
    </section> : <section className="data-panel administration-panel">
      <div className="section-heading">
        <h2>{t(`capacity.${activeTab}`)}</h2>
        {canEdit && activeProtection.canCreate && <button className="primary-button" type="button" onClick={openNew}>+ {t('administration.add')}</button>}
      </div>
      <PagedListControls applyLabel={t('recordList.applyFilters')}
        clearLabel={t('recordList.clearFilters')} rowsPerPageLabel={t('recordList.rowsPerPage')}
        previousLabel={t('recordList.previousPage')} nextLabel={t('recordList.nextPage')}
        pageLabel={t('recordList.pageOf')} totalLabel={t('recordList.totalRecords')}
        page={page} pageSize={pageSize} totalCount={snapshot.totalCount}
        formatNumber={formatNumber} filters={<><label><span>{t('nav.search')}</span>
          <input value={query} placeholder={t('recordList.search')}
            onChange={event => setQuery(event.target.value)} /></label>
          <label><span>{t('administration.status')}</span><select value={status}
            onChange={event => setStatus(event.target.value)}><option value="">
              {t('recordList.allStatuses')}</option><option value="active">{t('administration.active')}</option>
              <option value="inactive">{t('administration.inactive')}</option></select></label></>}
        onApply={() => setSearchParams(listParams(searchParams,{q:query.trim()||undefined,status:status||undefined,page:'1',pageSize:String(pageSize)}))}
        onClear={() => { setQuery(''); setStatus(''); setSearchParams(listParams(searchParams,{page:'1',pageSize:String(pageSize)})) }}
        onPageChange={next => setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:String(next),pageSize:String(pageSize)}))}
        onPageSizeChange={next => { rememberPageSize(next); setSearchParams(listParams(searchParams,{q:appliedQuery||undefined,status:appliedStatus||undefined,page:'1',pageSize:String(next)}))}} />
      <div className="administration-table-wrap">
      {activeTab === 'productionUnits' && <table className="data-table administration-table">
        <thead><tr><SortableHeaders sort={unitSort} columns={[["code",t('aplPanel.code')],["name",t('aplPanel.name')],["kind",t('capacity.unitKind')],["parent",t('capacity.parentUnit')],["centers",t('capacity.workCenters')],["status",t('administration.status')]]}/>{user?.capabilities.factoryView&&user.permissions.factoryView==='Write'&&<th>{t('factoryView.layout')}</th>}</tr></thead>
        <tbody>{unitSort.sortedRows.map(item => <tr key={item.id}>
          <td><Link className="record-code-link" to={`?${new URLSearchParams({...Object.fromEntries(searchParams),edit:item.id}).toString()}`} state={{recordIds:unitSort.sortedRows.map(value=>value.id)}}>{item.code}</Link></td>
          <td>{productionUnitPath(item, snapshot.productionUnits)}</td>
          <td>{t(`capacity.unitKind${productionUnitKinds.indexOf(item.kind)}` as Parameters<typeof t>[0])}</td>
          <td>{snapshot.productionUnits.find(parent => parent.id === item.parentId)?.name ?? '—'}</td>
          <td>{formatNumber(item.workCenterCount)}</td>
          <td><Status active={item.isActive} /></td>
          {user?.capabilities.factoryView&&user.permissions.factoryView==='Write'&&<td>{item.kind==='Section'?<Link className="secondary-button compact-button" to={`/app/situacion-fabrica?section=${item.id}&edit=1`}>{t('factoryView.configureLayout')}</Link>:'—'}</td>}
        </tr>)}</tbody>
      </table>}
      {activeTab === 'workCenters' && <table className="data-table administration-table">
        <thead><tr><SortableHeaders sort={centerSort} columns={[["code",t('aplPanel.code')],["name",t('aplPanel.name')],["planning",t('capacity.planningMode')],["capacity",t('capacity.capacityMode')],["efficiency",t('capacity.efficiencyPercent')],["resources",t('capacity.resources')],["status",t('administration.status')]]}/></tr></thead>
        <tbody>{centerSort.sortedRows.map((item) => <tr key={item.id}>
          <td><Link className="record-code-link" to={`?${new URLSearchParams({...Object.fromEntries(searchParams),edit:item.id}).toString()}`} state={{recordIds:centerSort.sortedRows.map(value=>value.id)}}>{item.code}</Link></td><td>{item.name}</td>
          <td>{t(item.planningMode === 'Aggregate' ? 'capacity.aggregate' : 'capacity.byResources')}</td>
          <td>{t(item.capacityMode === 'Infinite' ? 'capacity.infinite' : 'capacity.finite')}</td>
          <td>{formatNumber(item.efficiencyPercent)} %</td>
          <td>{formatNumber(item.resourceCount)}</td>
          <td><Status active={item.isActive} /></td>
        </tr>)}</tbody>
      </table>}
      {activeTab === 'resources' && <table className="data-table administration-table">
        <thead><tr><SortableHeaders sort={resourceSort} columns={[["code",t('aplPanel.code')],["name",t('aplPanel.name')],["kind",t('capacity.resourceKind')],["center",t('capacity.workCenter')],["parallel",t('capacity.parallelUnits')],["efficiency",t('capacity.efficiencyPercent')],["status",t('administration.status')]]}/></tr></thead>
        <tbody>{resourceSort.sortedRows.map((item) => <tr key={item.id}>
          <td><Link className="record-code-link" to={`?${new URLSearchParams({...Object.fromEntries(searchParams),edit:item.id}).toString()}`} state={{recordIds:resourceSort.sortedRows.map(value=>value.id)}}>{item.code}</Link></td><td>{item.name}</td>
          <td>{t(`capacity.resourceKind${resourceKinds.indexOf(item.kind)}` as Parameters<typeof t>[0])}</td>
          <td>{snapshot.workCenters.find(centerItem => centerItem.id === item.workCenterId)?.name ?? '—'}</td>
          <td>{formatNumber(item.parallelUnits)}</td>
          <td>{formatNumber(item.efficiencyPercent)} %</td>
          <td><Status active={item.isActive} /></td>
        </tr>)}</tbody>
      </table>}
      {activeTab === 'calendars' && <table className="data-table administration-table">
        <thead><tr><SortableHeaders sort={calendarSort} columns={[["code",t('aplPanel.code')],["name",t('aplPanel.name')],["timeZone",t('capacity.timeZone')],["hours",t('capacity.weeklyHours')],["status",t('administration.status')]]}/></tr></thead>
        <tbody>{calendarSort.sortedRows.map((item) => <tr key={item.id}>
          <td><Link className="record-code-link" to={`?${new URLSearchParams({...Object.fromEntries(searchParams),edit:item.id}).toString()}`} state={{recordIds:calendarSort.sortedRows.map(value=>value.id)}}>{item.code}</Link></td><td>{item.name}</td>
          <td>{item.timeZoneId}</td>
          <td>{formatNumber(item.shifts.reduce(
            (total, shift) => total + shift.endMinute - shift.startMinute, 0,
          ) / 60, { maximumFractionDigits: 2 })} h</td>
          <td><Status active={item.isActive} /></td>
        </tr>)}</tbody>
      </table>}
      </div>
      {snapshot[activeTab].length === 0 && <p className="empty-state">{t('administration.empty')}</p>}
    </section>}

    {modal === 'productionUnits' && <CapacityModal title={t(productionUnit
      ? 'capacity.editProductionUnit' : 'capacity.createProductionUnit')}
      wide error={error ? t(error as Parameters<typeof t>[0]) : null} onClose={closeEditor}
      onDelete={productionUnit && canEdit && activeProtection.canDelete ? () => remove(`/production-units/${productionUnit.id}`) : null}>
      <fieldset className={!canEdit ? 'readonly-detail' : undefined} disabled={!canEdit}><ProductionUnitForm key={productionUnit?.id ?? 'new'} item={productionUnit} snapshot={snapshot}
        save={(body) => execute(() => capacityCommand(productionUnit
          ? `/production-units/${productionUnit.id}` : '/production-units',
        productionUnit ? 'PUT' : 'POST', body))} /></fieldset>
    </CapacityModal>}
    {modal === 'workCenters' && <CapacityModal title={t(center
      ? 'capacity.editWorkCenter' : 'capacity.createWorkCenter')} wide error={error ? t(error as Parameters<typeof t>[0]) : null} onClose={closeEditor}
      onDelete={center && canEdit && activeProtection.canDelete ? () => remove(`/work-centers/${center.id}`) : null}>
      <fieldset className={!canEdit ? 'readonly-detail' : undefined} disabled={!canEdit}><WorkCenterForm key={center?.id ?? 'new'} item={center} snapshot={snapshot} busy={busy} save={(body) => execute(() =>
        capacityCommand(center ? `/work-centers/${center.id}` : '/work-centers',
          center ? 'PUT' : 'POST', body))} /></fieldset>
    </CapacityModal>}
    {modal === 'resources' && <CapacityModal title={t(resource
      ? 'capacity.editResource' : 'capacity.createResource')} wide error={error ? t(error as Parameters<typeof t>[0]) : null} onClose={closeEditor}
      onDelete={resource && canEdit && activeProtection.canDelete ? () => remove(`/resources/${resource.id}`) : null}>
      <fieldset className={!canEdit ? 'readonly-detail' : undefined} disabled={!canEdit}><ResourceForm key={resource?.id ?? 'new'} item={resource} snapshot={snapshot} busy={busy} save={(body) => execute(() =>
        capacityCommand(resource ? `/resources/${resource.id}` : '/resources',
          resource ? 'PUT' : 'POST', body))} /></fieldset>
    </CapacityModal>}
    {modal === 'calendars' && <CapacityModal title={t(calendar
      ? 'capacity.editCalendar' : 'capacity.createCalendar')} wide
      extraClassName="calendar-modal" error={error ? t(error as Parameters<typeof t>[0]) : null} onClose={closeEditor}
      onDelete={calendar && canEdit && activeProtection.canDelete ? () => remove(`/calendars/${calendar.id}`) : null}>
      <fieldset className={!canEdit ? 'readonly-detail' : undefined} disabled={!canEdit}><CalendarForm key={calendar?.id ?? 'new'} item={calendar} weekStartsOn={snapshot.weekStartsOn}
        busy={busy} save={(body) => execute(() =>
        capacityCommand(calendar ? `/calendars/${calendar.id}` : '/calendars',
          calendar ? 'PUT' : 'POST', body))} /></fieldset>
    </CapacityModal>}
    {confirmationDialog}
  </main>
}

function Status({ active }: { active: boolean }) {
  const { t } = useLocale()
  return <span className={`status-pill ${active ? 'active' : 'suspended'}`}>
    {t(active ? 'administration.active' : 'administration.inactive')}
  </span>
}

function productionUnitPath(
  unit: ProductionUnitItem,
  units: ProductionUnitItem[],
) {
  const names = [unit.name]
  const visited = new Set([unit.id])
  let parent = units.find(candidate => candidate.id === unit.parentId)
  while (parent && !visited.has(parent.id)) {
    visited.add(parent.id)
    names.unshift(parent.name)
    parent = units.find(candidate => candidate.id === parent?.parentId)
  }
  return names.join(' › ')
}

function ProductionUnitForm({ item, snapshot, save }: {
  item: ProductionUnitItem | null
  snapshot: CapacitySnapshot
  save: (body: unknown) => Promise<void>
}) {
  const { t } = useLocale()
  const { user } = useAuth()
  const [form, setForm] = useState({
    code: item?.code ?? '',
    name: item?.name ?? '',
    description: item?.description ?? '',
    kind: item?.kind ?? 'Plant',
    parentId: item?.parentId ?? '',
    timeZoneId: item?.timeZoneId ?? '',
    isActive: item?.isActive ?? true,
  })
  return <IntegrationProtectionBoundary entity="ProductionUnit" fields={['code','name','kind','parentCode','timeZoneId','description','isActive']}><form className="administration-form modal-form" onSubmit={event => {
    event.preventDefault()
    void save({
      ...form,
      code: item ? null : form.code,
      parentId: form.parentId || null,
      timeZoneId: form.timeZoneId || null,
    })
  }}>
    <div className="compact-form-grid">
      <label><span>{t('aplPanel.code')}</span><input required disabled={Boolean(item)}
        value={form.code} onChange={event => setForm({
          ...form, code: event.target.value,
        })} /></label>
      <label><span>{t('aplPanel.name')}</span><input required value={form.name}
        onChange={event => setForm({ ...form, name: event.target.value })} /></label>
      <label><span>{t('capacity.unitKind')}</span><select value={form.kind}
        onChange={event => setForm({
          ...form,
          kind: event.target.value as typeof productionUnitKinds[number],
        })}>
        {productionUnitKinds.map((kind, index) => <option key={kind} value={kind}>
          {t(`capacity.unitKind${index}` as Parameters<typeof t>[0])}
        </option>)}
      </select></label>
      <label><span>{t('capacity.parentUnit')}</span><select value={form.parentId}
        onChange={event => setForm({ ...form, parentId: event.target.value })}>
        <option value="">—</option>
        {snapshot.productionUnits.filter(unit =>
          unit.isActive && unit.id !== item?.id).map(unit =>
          <option key={unit.id} value={unit.id}>
            {unit.code} · {productionUnitPath(unit, snapshot.productionUnits)}
          </option>)}
      </select></label>
      <label><span>{t('capacity.timeZone')}</span><select value={form.timeZoneId}
        onChange={event => setForm({ ...form, timeZoneId: event.target.value })}>
        <option value="">{t('capacity.inheritTimeZone')}</option>
        <option value="Romance Standard Time">Europe/Madrid</option>
        <option value="GMT Standard Time">Europe/London</option>
        <option value="W. Europe Standard Time">Europe/Berlin, Paris, Rome</option>
        <option value="UTC">UTC</option>
      </select></label>
    </div>
    <label><span>{t('capacity.description')}</span><textarea rows={3}
      value={form.description} onChange={event => setForm({
        ...form, description: event.target.value,
      })} /></label>
    <label className="checkbox-row"><input type="checkbox" checked={form.isActive}
      onChange={event => setForm({ ...form, isActive: event.target.checked })} />
      <span>{t('administration.active')}</span></label>
    {item?.kind === 'Section' && user?.capabilities.factoryView && user.permissions.factoryView === 'Write' &&
      <div className="form-actions factory-layout-link-action">
        <Link className="secondary-button" to={`/app/situacion-fabrica?section=${item.id}&edit=1`}>
          {t('factoryView.configureLayout')}
        </Link>
      </div>}
  </form></IntegrationProtectionBoundary>
}

function WorkCenterForm({ item, snapshot, busy, save }: {
  item: WorkCenterItem | null; snapshot: CapacitySnapshot
  busy: boolean; save: (body: unknown) => Promise<void>
}) {
  const { t, locale } = useLocale()
  const [form, setForm] = useState({
    code: item?.code ?? '',
    name: item?.name ?? '',
    description: item?.description ?? '',
    productionUnitId: item?.productionUnitId ?? '',
    defaultCalendarId: item?.defaultCalendarId ?? '',
    capacityMode: item?.capacityMode ?? 'Finite',
    planningMode: item?.planningMode ?? 'ByResources',
    efficiencyPercent: item?.efficiencyPercent ?? 100,
    hourlyCost: item?.hourlyCost ?? 0,
    queueTimeMinutes: item?.queueTimeMinutes ?? 0,
    maximumWorkInProgress: item?.maximumWorkInProgress ?? '',
    ownership: item?.ownership ?? 'Internal',
    isInformational: item?.isInformational ?? false,
    isBottleneck: item?.isBottleneck ?? false,
    isActive: item?.isActive ?? true,
  })
  return <IntegrationProtectionBoundary entity="WorkCenter" fields={['code','name','description','productionUnitCode','capacityMode','planningMode','ownership','calendarCode','efficiencyPercent','hourlyCost','queueTimeMinutes','maximumWorkInProgress','isInformational','isBottleneck','isActive']}><form className="administration-form modal-form" onSubmit={(event) => {
    event.preventDefault(); void save({
      ...form,
      code: item ? null : form.code,
      productionUnitId: form.productionUnitId || null,
      defaultCalendarId: form.defaultCalendarId || null,
      maximumWorkInProgress: form.maximumWorkInProgress === ''
        ? null : Number(form.maximumWorkInProgress),
    })
  }}>
    <label><span>{t('aplPanel.code')}</span><input required disabled={Boolean(item)}
      value={form.code} onChange={event => setForm({ ...form, code: event.target.value })} /></label>
    <label><span>{t('aplPanel.name')}</span><input required value={form.name}
      onChange={event => setForm({ ...form, name: event.target.value })} /></label>
    <label><span>{t('capacity.description')}</span><textarea rows={3}
      value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} /></label>
    <div className="compact-form-grid">
      <label><span>{t('capacity.productionUnit')}</span><select
        value={form.productionUnitId} onChange={event => setForm({
          ...form, productionUnitId: event.target.value,
        })}>
        <option value="">—</option>{snapshot.productionUnits.filter(x => x.isActive).map(x =>
          <option key={x.id} value={x.id}>
            {x.code} · {productionUnitPath(x, snapshot.productionUnits)}
          </option>)}
      </select></label>
      <label><span>{t('capacity.capacityMode')}</span><select value={form.capacityMode}
        onChange={event => setForm({
          ...form, capacityMode: event.target.value as 'Finite' | 'Infinite',
        })}>
        <option value="Finite">{t('capacity.finite')}</option>
        <option value="Infinite">{t('capacity.infinite')}</option>
      </select></label>
      <label><span>{t('capacity.planningMode')}</span><select value={form.planningMode}
        onChange={event => setForm({ ...form,
          planningMode: event.target.value as 'ByResources' | 'Aggregate',
        })}>
        <option value="ByResources">{t('capacity.byResources')}</option>
        <option value="Aggregate">{t('capacity.aggregate')}</option>
      </select></label>
      <label><span>{t('subcontracting.ownership')}</span><select value={form.ownership}
        onChange={event => setForm({ ...form, ownership: event.target.value as 'Internal' | 'External' })}>
        <option value="Internal">{t('subcontracting.internal')}</option>
        <option value="External">{t('subcontracting.external')}</option>
      </select></label>
      <label><span>{t('capacity.defaultCalendar')}</span><select value={form.defaultCalendarId}
        onChange={event => setForm({ ...form, defaultCalendarId: event.target.value })}>
        <option value="">—</option>{snapshot.calendars.filter(x => x.isActive).map(x =>
          <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
      </select></label>
      <label><span>{t('capacity.efficiencyPercent')}</span><LocalizedDecimalInput locale={locale}
        value={form.efficiencyPercent} min={0.01} max={500}
        onChange={efficiencyPercent => setForm({ ...form, efficiencyPercent })} /></label>
      <label><span>{t('capacity.hourlyCost')}</span><LocalizedDecimalInput locale={locale}
        value={form.hourlyCost} min={0} max={1000000}
        onChange={hourlyCost => setForm({ ...form, hourlyCost })} /></label>
      <label><span>{t('capacity.queueTimeMinutes')}</span><input type="number" min="0" step="any"
        value={form.queueTimeMinutes} onChange={event => setForm({
          ...form, queueTimeMinutes: Number(event.target.value),
        })} /></label>
      <label><span>{t('capacity.maximumWorkInProgress')}</span><input type="number" min="1"
        value={form.maximumWorkInProgress} onChange={event => setForm({
          ...form, maximumWorkInProgress: event.target.value,
        })} /></label>
    </div>
    <label className="checkbox-row"><input type="checkbox" checked={form.isInformational}
      onChange={event => setForm({ ...form, isInformational: event.target.checked })} />
      <span>{t('capacity.informational')}</span></label>
    <p className="form-help">{t('capacity.informationalHelp')}</p>
    <label className="checkbox-row"><input type="checkbox" checked={form.isBottleneck}
      onChange={event => setForm({ ...form, isBottleneck: event.target.checked })} />
      <span>{t('capacity.bottleneck')}</span></label>
    <label className="checkbox-row"><input type="checkbox" checked={form.isActive}
      onChange={event => setForm({ ...form, isActive: event.target.checked })} /><span>{t('administration.active')}</span></label>
    <button className="primary-button" disabled={busy}>{t('administration.save')}</button>
  </form></IntegrationProtectionBoundary>
}

function ResourceForm({ item, snapshot, busy, save }: {
  item: ResourceItem | null; snapshot: CapacitySnapshot; busy: boolean
  save: (body: unknown) => Promise<void>
}) {
  const { t, locale } = useLocale()
  const [form, setForm] = useState({
    code: item?.code ?? '', name: item?.name ?? '',
    description: item?.description ?? '',
    kind: item?.kind ?? 'Machine',
    workCenterId: item?.workCenterId ?? '', calendarId: item?.calendarId ?? '',
    capacity: item?.capacity ?? 1,
    parallelUnits: item?.parallelUnits ?? 1,
    efficiencyPercent: item?.efficiencyPercent ?? 100,
    hourlyCost: item?.hourlyCost ?? 0,
    setupTimeMinutes: item?.setupTimeMinutes ?? 0,
    queueTimeMinutes: item?.queueTimeMinutes ?? 0,
    ownership: item?.ownership ?? 'Internal',
    isInformational: item?.isInformational ?? false,
    isFinite: item?.isFinite ?? true,
    isBottleneck: item?.isBottleneck ?? false,
    isActive: item?.isActive ?? true,
  })
  const [setupTransitions, setSetupTransitions] = useState(
    item?.setupTransitions ?? [])
  return <IntegrationProtectionBoundary entity="Resource" fields={['code','name','description','kind','workCenterCode','calendarCode','ownership','capacity','parallelUnits','efficiencyPercent','hourlyCost','setupTimeMinutes','queueTimeMinutes','isInformational','isFinite','isBottleneck','isActive']}><form className="administration-form modal-form" onSubmit={(event) => {
    event.preventDefault(); void save({ ...form, setupTransitions,
      code: item ? null : form.code,
      workCenterId: form.workCenterId || null, calendarId: form.calendarId || null })
  }}>
    <label><span>{t('aplPanel.code')}</span><input required disabled={Boolean(item)}
      value={form.code} onChange={event => setForm({ ...form, code: event.target.value })} /></label>
    <label><span>{t('aplPanel.name')}</span><input required value={form.name}
      onChange={event => setForm({ ...form, name: event.target.value })} /></label>
    <label><span>{t('capacity.description')}</span><textarea rows={3}
      value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} /></label>
    <div className="compact-form-grid">
      <label><span>{t('capacity.resourceKind')}</span><select value={form.kind}
        onChange={event => setForm({
          ...form,
          kind: event.target.value as typeof resourceKinds[number],
        })}>
        {resourceKinds.map((kind, index) => <option key={kind} value={kind}>
          {t(`capacity.resourceKind${index}` as Parameters<typeof t>[0])}
        </option>)}
      </select></label>
      <label><span>{t('capacity.workCenter')}</span><select value={form.workCenterId}
        onChange={event => setForm({ ...form, workCenterId: event.target.value })}>
        <option value="">—</option>{snapshot.workCenters.filter(x => x.isActive).map(x =>
          <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
      </select></label>
      <label><span>{t('capacity.calendar')}</span><select value={form.calendarId}
        onChange={event => setForm({ ...form, calendarId: event.target.value })}>
        <option value="">—</option>{snapshot.calendars.filter(x => x.isActive).map(x =>
          <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
      </select></label>
      <label><span>{t('subcontracting.ownership')}</span><select value={form.ownership}
        onChange={event => setForm({ ...form, ownership: event.target.value as 'Internal' | 'External' })}>
        <option value="Internal">{t('subcontracting.internal')}</option>
        <option value="External">{t('subcontracting.external')}</option>
      </select></label>
    </div>
    <section className="resource-setup-matrix" aria-label={t('capacity.setupMatrix')}>
      <div className="collection-heading"><div><h3>{t('capacity.setupMatrix')}</h3>
        <p>{t('capacity.setupMatrixHelp')}</p></div><button type="button"
        className="secondary-button" onClick={() => setSetupTransitions(current =>
          [...current, { fromFamily: '', toFamily: '', changeoverMinutes: 0,
            minimumCampaignQuantity: 0 }])}>+ {t('capacity.addTransition')}</button></div>
      {setupTransitions.map((transition, index) => <div className="compact-form-grid"
        key={`${index}-${transition.fromFamily}-${transition.toFamily}`}>
        <label><span>{t('capacity.fromFamily')}</span><input required maxLength={80}
          value={transition.fromFamily} onChange={event => setSetupTransitions(current =>
            current.map((value, i) => i === index ? { ...value,
              fromFamily: event.target.value } : value))} /></label>
        <label><span>{t('capacity.toFamily')}</span><input required maxLength={80}
          value={transition.toFamily} onChange={event => setSetupTransitions(current =>
            current.map((value, i) => i === index ? { ...value,
              toFamily: event.target.value } : value))} /></label>
        <label><span>{t('capacity.changeoverMinutes')}</span><LocalizedDecimalInput
          locale={locale} min={0} value={transition.changeoverMinutes}
          onChange={changeoverMinutes => setSetupTransitions(current => current.map(
            (value, i) => i === index ? { ...value, changeoverMinutes } : value))} /></label>
        <label><span>{t('capacity.minimumCampaignQuantity')}</span><LocalizedDecimalInput
          locale={locale} min={0} value={transition.minimumCampaignQuantity}
          onChange={minimumCampaignQuantity => setSetupTransitions(current => current.map(
            (value, i) => i === index ? { ...value, minimumCampaignQuantity } : value))} /></label>
        <button type="button" className="danger-link" aria-label={t('capacity.removeTransition')}
          onClick={() => setSetupTransitions(current => current.filter((_, i) => i !== index))}>
          {t('capacity.removeTransition')}</button>
      </div>)}
    </section>
    <div className="compact-form-grid">
      <label><span>{t('capacity.capacity')}</span><LocalizedDecimalInput locale={locale}
        value={form.capacity} min={0.0001} max={100000}
        onChange={capacity => setForm({ ...form, capacity })} /></label>
      <label><span>{t('capacity.parallelUnits')}</span><input type="number" min="1" max="10000"
        value={form.parallelUnits} onChange={event => setForm({
          ...form, parallelUnits: Number(event.target.value),
        })} /></label>
      <label><span>{t('capacity.efficiencyPercent')}</span><LocalizedDecimalInput locale={locale}
        value={form.efficiencyPercent} min={0.01} max={500}
        onChange={efficiencyPercent => setForm({ ...form, efficiencyPercent })} /></label>
      <label><span>{t('capacity.hourlyCost')}</span><LocalizedDecimalInput locale={locale}
        value={form.hourlyCost} min={0} max={1000000}
        onChange={hourlyCost => setForm({ ...form, hourlyCost })} /></label>
      <label><span>{t('capacity.setupTimeMinutes')}</span><input type="number" min="0" step="any"
        value={form.setupTimeMinutes} onChange={event => setForm({
          ...form, setupTimeMinutes: Number(event.target.value),
        })} /></label>
      <label><span>{t('capacity.queueTimeMinutes')}</span><input type="number" min="0" step="any"
        value={form.queueTimeMinutes} onChange={event => setForm({
          ...form, queueTimeMinutes: Number(event.target.value),
        })} /></label>
    </div>
    <label className="checkbox-row"><input type="checkbox" checked={form.isInformational}
      onChange={event => setForm({ ...form, isInformational: event.target.checked })} />
      <span>{t('capacity.informational')}</span></label>
    <p className="form-help">{t('capacity.informationalHelp')}</p>
    <label className="checkbox-row"><input type="checkbox" checked={form.isFinite}
      onChange={event => setForm({ ...form, isFinite: event.target.checked })} />
      <span>{t('capacity.finiteCapacity')}</span></label>
    <label className="checkbox-row"><input type="checkbox" checked={form.isBottleneck}
      onChange={event => setForm({ ...form, isBottleneck: event.target.checked })} />
      <span>{t('capacity.bottleneck')}</span></label>
    <label className="checkbox-row"><input type="checkbox" checked={form.isActive}
      onChange={event => setForm({ ...form, isActive: event.target.checked })} />
      <span>{t('administration.active')}</span></label>
    <button className="primary-button" disabled={busy}>{t('administration.save')}</button>
  </form></IntegrationProtectionBoundary>
}

function CalendarForm({ item, weekStartsOn, busy, save }: {
  item: CalendarItem | null; weekStartsOn: 'Sunday' | 'Monday'
  busy: boolean; save: (body: unknown) => Promise<void>
}) {
  const { t, locale } = useLocale()
  const orderedDays = weekStartsOn === 'Sunday'
    ? [0, 1, 2, 3, 4, 5, 6]
    : [1, 2, 3, 4, 5, 6, 0]
  const [form, setForm] = useState({
    code: item?.code ?? '', name: item?.name ?? '',
    timeZoneId: item?.timeZoneId ?? 'Romance Standard Time',
    validFrom: item?.validFrom ?? '',
    validUntil: item?.validUntil ?? '',
    isActive: item?.isActive ?? true,
  })
  const [shifts, setShifts] = useState(
    () => [...(item?.shifts ?? defaultShifts)].sort((left, right) =>
      orderedDays.indexOf(left.dayOfWeek) - orderedDays.indexOf(right.dayOfWeek)),
  )
  const [exceptions, setExceptions] = useState<CalendarException[]>(
    item?.exceptions ?? [],
  )
  const updateShift = (index: number, change: Partial<CalendarShift>) =>
    setShifts(current => current.map((shift, shiftIndex) =>
      shiftIndex === index ? { ...shift, ...change } : shift))
  const applyShiftToAll = (source: CalendarShift) =>
    setShifts(current => [...new Set(current.map(shift => shift.dayOfWeek))]
      .map(dayOfWeek => ({
      dayOfWeek,
      startMinute: source.startMinute,
      endMinute: source.endMinute,
      capacityPercent: source.capacityPercent,
      efficiencyPercent: source.efficiencyPercent,
      yieldPercent: source.yieldPercent,
    })))
  const configureContinuousExistingDays = () =>
    setShifts(current => [...new Set(current.map(shift => shift.dayOfWeek))]
      .sort((left, right) =>
        orderedDays.indexOf(left) - orderedDays.indexOf(right))
      .map(dayOfWeek => ({
        dayOfWeek,
        startMinute: 0,
        endMinute: 1440,
        capacityPercent: 100,
        efficiencyPercent: 100, yieldPercent: 100,
      })))
  const configureContinuousOperation = () =>
    setShifts(orderedDays.map(dayOfWeek => ({
      dayOfWeek,
      startMinute: 0,
      endMinute: 1440,
      capacityPercent: 100,
      efficiencyPercent: 100, yieldPercent: 100,
    })))
  return <form className="administration-form modal-form" onSubmit={(event) => {
    event.preventDefault(); void save({
      ...form,
      code: item ? null : form.code,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
      shifts,
      exceptions,
    })
  }}>
    <IntegrationProtectionBoundary entity="ProductionCalendar" fields={['code','name']}><div className="compact-form-grid">
      <label><span>{t('aplPanel.code')}</span><input required disabled={Boolean(item)}
        value={form.code} onChange={event => setForm({ ...form, code: event.target.value })} /></label>
      <label><span>{t('aplPanel.name')}</span><input required value={form.name}
        onChange={event => setForm({ ...form, name: event.target.value })} /></label>
    </div></IntegrationProtectionBoundary>
    <IntegrationProtectionBoundary entity="ProductionCalendar" fields={['timeZoneId']}><label><span>{t('capacity.timeZone')}</span><select value={form.timeZoneId}
      onChange={event => setForm({ ...form, timeZoneId: event.target.value })}>
      <option value="Romance Standard Time">Europe/Madrid</option>
      <option value="GMT Standard Time">Europe/London</option>
      <option value="W. Europe Standard Time">Europe/Berlin, Paris, Rome</option>
      <option value="UTC">UTC</option>
    </select></label></IntegrationProtectionBoundary>
    <IntegrationProtectionBoundary entity="ProductionCalendar" fields={['validFrom','validUntil']}><div className="compact-form-grid">
      <label><span>{t('capacity.validFrom')}</span><LocalizedDateInput
        locale={locale} weekStartsOn={weekStartsOn}
        ariaLabel={t('capacity.validFrom')} pickerLabel={t('input.selectDate')}
        previousMonthLabel={t('input.previousMonth')}
        nextMonthLabel={t('input.nextMonth')} value={form.validFrom}
        onChange={validFrom => setForm({ ...form, validFrom })} /></label>
      <label><span>{t('capacity.validUntil')}</span><LocalizedDateInput
        locale={locale} weekStartsOn={weekStartsOn}
        ariaLabel={t('capacity.validUntil')} pickerLabel={t('input.selectDate')}
        previousMonthLabel={t('input.previousMonth')}
        nextMonthLabel={t('input.nextMonth')} value={form.validUntil}
        onChange={validUntil => setForm({ ...form, validUntil })} /></label>
    </div></IntegrationProtectionBoundary>
    <div className="shift-editor">
      <div className="shift-row shift-header" aria-hidden="true">
        <span>{t('capacity.day')}</span>
        <span>{t('capacity.startTime')}</span>
        <span>{t('capacity.endTime')}</span>
        <span>{t('capacity.capacityPercent')}</span>
        <span>{t('capacity.efficiencyPercent')}</span>
        <span>{t('capacity.yieldPercent')}</span><span />
      </div>
      {shifts.map((shift, index) => <IntegrationProtectionBoundary entity="CalendarShift" fields={['dayOfWeek','startMinute','endMinute','capacityPercent']} key={`${shift.dayOfWeek}-${index}`}><div className="shift-row">
        <select value={shift.dayOfWeek} onChange={event => updateShift(index,
          { dayOfWeek: Number(event.target.value) })}>
          {orderedDays.map(day =>
            <option key={day} value={day}>{t(
              `capacity.day${day}` as Parameters<typeof t>[0],
            )}</option>)}
        </select>
        <LocalizedTimeInput
          locale={locale}
          value={shift.startMinute}
          ariaLabel={t('capacity.startTime')}
          pickerLabel={t('input.selectTime')}
          hourLabel={t('input.hour')}
          minuteLabel={t('input.minute')}
          dayPeriodLabel={t('input.dayPeriod')}
          onChange={startMinute => updateShift(index, { startMinute })}
        />
        <LocalizedTimeInput
          locale={locale}
          value={shift.endMinute}
          allowEndOfDay
          ariaLabel={t('capacity.endTime')}
          pickerLabel={t('input.selectTime')}
          hourLabel={t('input.hour')}
          minuteLabel={t('input.minute')}
          dayPeriodLabel={t('input.dayPeriod')}
          onChange={endMinute => updateShift(index, { endMinute })}
        />
        <input type="number" min="1" max="100" value={shift.capacityPercent}
          aria-label={t('capacity.capacityPercent')}
          onChange={event => updateShift(index, { capacityPercent: Number(event.target.value) })} />
        <input type="number" min="1" max="1000" value={shift.efficiencyPercent}
          aria-label={t('capacity.efficiencyPercent')}
          onChange={event => updateShift(index, { efficiencyPercent: Number(event.target.value) })} />
        <input type="number" min="1" max="1000" value={shift.yieldPercent}
          aria-label={t('capacity.yieldPercent')}
          onChange={event => updateShift(index, { yieldPercent: Number(event.target.value) })} />
        <div className="shift-actions">
          <button className="shift-action-button" type="button"
            title={t('capacity.applyShiftToAll')}
            aria-label={t('capacity.applyShiftToAll')}
            onClick={() => applyShiftToAll(shift)}>⇉</button>
          <button className="modal-close" type="button"
            aria-label={t('capacity.removeShift')}
            onClick={() => setShifts(current => current.filter((_, i) => i !== index))}>×</button>
        </div>
      </div></IntegrationProtectionBoundary>)}
      <div className="shift-editor-actions">
        <button className="secondary-button" type="button" onClick={() => setShifts(current =>
          [...current, { dayOfWeek: 1, startMinute: 480, endMinute: 960,
            capacityPercent: 100, efficiencyPercent: 100, yieldPercent: 100 }])}>
          + {t('capacity.addShift')}
        </button>
        <button className="secondary-button" type="button"
          onClick={configureContinuousExistingDays}>
          {t('capacity.configure24ExistingDays')}
        </button>
        <button className="secondary-button" type="button"
          onClick={configureContinuousOperation}>
          {t('capacity.configure247')}
        </button>
      </div>
    </div>
    <section className="calendar-exceptions">
      <div className="section-heading">
        <h3>{t('capacity.exceptions')}</h3>
        <button className="secondary-button" type="button" onClick={() =>
          setExceptions(current => [...current, {
            date: new Date().toISOString().slice(0, 10),
            isWorking: false,
            startMinute: 0,
            endMinute: 1440,
            capacityPercent: 100,
            description: '',
          }])}>+ {t('capacity.addException')}</button>
      </div>
      {exceptions.map((exception, index) => <IntegrationProtectionBoundary entity="CalendarException" fields={['date','isWorking','startMinute','endMinute','capacityPercent','description']}
        key={`${exception.date}-${index}`}><div className="exception-row">
        <LocalizedDateInput locale={locale} weekStartsOn={weekStartsOn}
          ariaLabel={t('capacity.exceptionDate')}
          pickerLabel={t('input.selectDate')}
          previousMonthLabel={t('input.previousMonth')}
          nextMonthLabel={t('input.nextMonth')}
          value={exception.date} onChange={date => setExceptions(current =>
            current.map((item, itemIndex) => itemIndex === index
              ? { ...item, date } : item))} />
        <select aria-label={t('capacity.exceptionType')}
          value={exception.isWorking ? 'working' : 'closed'}
          onChange={event => setExceptions(current => current.map(
            (item, itemIndex) => itemIndex === index
              ? { ...item, isWorking: event.target.value === 'working' } : item,
          ))}>
          <option value="closed">{t('capacity.closed')}</option>
          <option value="working">{t('capacity.specialOpening')}</option>
        </select>
        <LocalizedTimeInput locale={locale} value={exception.startMinute}
          ariaLabel={t('capacity.startTime')} pickerLabel={t('input.selectTime')}
          hourLabel={t('input.hour')} minuteLabel={t('input.minute')}
          dayPeriodLabel={t('input.dayPeriod')}
          onChange={startMinute => setExceptions(current => current.map(
            (item, itemIndex) => itemIndex === index
              ? { ...item, startMinute } : item,
          ))} />
        <LocalizedTimeInput locale={locale} value={exception.endMinute}
          allowEndOfDay ariaLabel={t('capacity.endTime')}
          pickerLabel={t('input.selectTime')} hourLabel={t('input.hour')}
          minuteLabel={t('input.minute')} dayPeriodLabel={t('input.dayPeriod')}
          onChange={endMinute => setExceptions(current => current.map(
            (item, itemIndex) => itemIndex === index
              ? { ...item, endMinute } : item,
          ))} />
        <input type="number" min="1" max="100"
          aria-label={t('capacity.capacityPercent')}
          value={exception.capacityPercent}
          onChange={event => setExceptions(current => current.map(
            (item, itemIndex) => itemIndex === index
              ? { ...item, capacityPercent: Number(event.target.value) } : item,
          ))} />
        <input type="text" aria-label={t('capacity.description')}
          placeholder={t('capacity.description')} value={exception.description}
          onChange={event => setExceptions(current => current.map(
            (item, itemIndex) => itemIndex === index
              ? { ...item, description: event.target.value } : item,
          ))} />
        <button className="modal-close" type="button"
          aria-label={t('capacity.removeException')}
          onClick={() => setExceptions(current =>
            current.filter((_, itemIndex) => itemIndex !== index))}>×</button>
      </div></IntegrationProtectionBoundary>)}
      {exceptions.length === 0 && <p className="empty-state">
        {t('capacity.noExceptions')}
      </p>}
    </section>
    <IntegrationProtectionBoundary entity="ProductionCalendar" fields={['isActive']}><label className="checkbox-row"><input type="checkbox" checked={form.isActive}
      onChange={event => setForm({ ...form, isActive: event.target.checked })} />
      <span>{t('administration.active')}</span></label></IntegrationProtectionBoundary>
    <button className="primary-button" disabled={busy}>{t('administration.save')}</button>
  </form>
}

function CapacityModal({ title, wide = false, extraClassName = '', error, onClose, onDelete, children }: {
  title: string; wide?: boolean; extraClassName?: string; error?: ReactNode; onClose: () => void; onDelete?: (() => void) | null; children: ReactNode
}) {
  const { t } = useLocale()
  const { user } = useAuth()
  return <AppModal title={title} closeLabel={t('nav.closeMenu')} onClose={onClose}
    wide={wide} extraClassName={extraClassName} feedback={error}
    saveForm={user?.permissions.capacity === 'Write'} onDelete={onDelete}>{children}</AppModal>
}
